import { useMemo, useState } from 'react';
import { message } from 'antd';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUserId } from 'shared/store/selectors/user.selector';
import { formatNumber } from 'shared/utils';
import { buildManualCheckInOptions } from 'shared/utils/manual-check-in-options';
import { useManualCheckIn } from 'shared/hooks/attendance.hooks';
import { EVENTS, SCHEDULE_EVENT_TYPES } from 'shared/constants';
import { Button, MultiSelectWithFooter } from 'src/components/design-system';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';

export function ManualCheckIn({ eventId, users, checkedInUserIds }) {
  const attendeesLang = useSelector(getLang('CHECKED_IN_ATTENDEES'));
  const createLang = useSelector(getLang('CREATE_EDUCATIONAL_EVENT'));
  const currentUserId = useSelector(getUserId);
  const [selectedIds, setSelectedIds] = useState([]);

  const options = useMemo(
    () =>
      buildManualCheckInOptions({
        users,
        checkedInUserIds,
        currentUserId,
        youLabel: createLang.YOU,
      }),
    [users, checkedInUserIds, currentUserId, createLang.YOU],
  );

  const mutation = useManualCheckIn(eventId);

  const handleOpenChange = open => {
    if (open) {
      analyticsService.track(EVENTS.SCHEDULE.MANUAL_CHECK_IN_OPEN, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        eventId,
      });
    }
  };

  const submit = async () => {
    try {
      const { failureIds } = await mutation.mutateAsync({ userIds: selectedIds });
      const succeededIds = selectedIds.filter(id => !failureIds.includes(id));
      if (succeededIds.length) {
        analyticsService.track(EVENTS.SCHEDULE.MANUAL_CHECK_IN_SUBMIT, {
          type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
          eventId,
          userIds: succeededIds,
        });
      }
      setSelectedIds(failureIds);
      if (failureIds.length) {
        monitorService.error('Manual check-in failed for some users', {
          count: failureIds.length,
          failures: failureIds,
        });
        message.error(
          attendeesLang.MANUAL_CHECK_IN_FAILED.replace('{count}', formatNumber(failureIds.length)),
        );
      }
    } catch (error) {
      monitorService.error('Manual check-in request failed', { error });
      message.error(attendeesLang.MANUAL_CHECK_IN_GENERIC_FAILED);
    }
  };

  return (
    <MultiSelectWithFooter
      name="manualCheckIn"
      placeholder={attendeesLang.MANUAL_SEARCH_PLACEHOLDER}
      options={options}
      value={selectedIds}
      onChange={(name, value) => setSelectedIds(value)}
      onOpenChange={handleOpenChange}
      renderTrigger={() => (
        <Button size="small" type="primary" ghost>
          {attendeesLang.MANUAL_TITLE}
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      )}
      footer={
        <Button
          type="primary"
          block
          disabled={!selectedIds.length}
          loading={mutation.isPending}
          onClick={submit}>
          {attendeesLang.MANUAL_SUBMIT.replace('{count}', formatNumber(selectedIds.length))}
        </Button>
      }
    />
  );
}
